/**
 * Checks if value is `number`, acts as a typescript safeguard.
 *
 * @example
 * ```
 * [1, 'b', 2].filter(isNumber)
 * > [1, 2]
 * ```
 * @category Guard
 */
export function isNumber(value) {
    return typeof value === 'number';
}
/**
 * Checks if value is `boolean`, acts as a typescript safeguard.
 *
 * @example
 * ```
 * [true, 'b', false].filter(isBoolean)
 * > [true, false]
 * ```
 * @category Guard
 */
export function isBoolean(value) {
    return typeof value === 'boolean';
}
/**
 * Checks if value is `string`, acts as a typescript safeguard.
 *
 * @example
 * ```
 * [1, 'b', false].filter(isString)
 * > ['b']
 * ```
 * @category Guard
 */
export function isString(value) {
    return typeof value === 'string';
}
/**
 * Checks if value is `array`, acts as a typescript safeguard.
 *
 * @example
 * ```
 * [[1], ['b'], false].filter(isArray)
 * > [[1], ['b']]
 * ```
 * @category Guard
 */
export function isArray(value) {
    return value instanceof Array;
}
/**
 * Checks if value is instance of `Error`, acts as a typescript safeguard.
 *
 * @example
 * ```
 * [new Error('error'), 1, 5].filter(isError)
 * > [new Error('error')]
 *
 * [new Error('error'), 1, 5].filter(isNot(isError))
 * > [1, 5]
 * ```
 * @category Guard
 */
export function isError(data) {
    return data instanceof Error;
}
/**
 * Checks if value is `undefined`, acts as a typescript safeguard.
 *
 * @example
 * ```
 * [null, undefined, 1, 5].filter(isUndefined)
 * > [undefined]
 *
 * [null, undefined, 1, 5].filter(isNot(isUndefined))
 * > [null, 1, 5]
 * ```
 * @category Guard
 */
export function isUndefined(value) {
    return value === undefined;
}
/**
 * Checks if value is `null`, acts as a typescript safeguard.
 *
 * @example
 * ```
 * [null, undefined, 1, 5].filter(isUndefined)
 * > [undefined]
 *
 * [null, undefined, 1, 5].filter(isNot(isUndefined))
 * > [null, 1, 5]
 * ```
 * @category Guard
 */
export function isNull(value) {
    return value === null;
}
/**
 * Checks if value is not `null` or `undefined`, acts as a typescript safeguard.
 *
 * @example
 * ```
 * [null, undefined, 1, 5].filter(isUndefined)
 * > [null, undefined]
 *
 * [null, undefined, 1, 5].filter(isNot(isUndefined))
 * > [1, 5]
 * ```
 * @category Guard
 */
export function isNullOrUndefined(value) {
    return value == null;
}
/**
 * Checks if the value is an empty.
 *
 * Supports following types:
 * * Object - `false` if object is empty
 * * Array - `false` if array is empty
 * * Boolean - `false` if boolean is `false`
 * * Number - `false` if string is `''`
 * * Number - `false` if number is `0`
 *
 * @category Guard
 */
export function isEmpty(value) {
    if (value == null) {
        return false;
    }
    if (typeof value === 'boolean') {
        return value === false;
    }
    if (typeof value === 'string') {
        return value === '';
    }
    if (typeof value === 'number') {
        return value === 0;
    }
    if (value instanceof Array) {
        return value.length === 0;
    }
    if (isPlainObject(value)) {
        return Object.keys(value).length === 0;
    }
    return false;
}
/**
 * Inverse guard
 *
 * @example
 * ```
 * [new Error('Error'), 1, 2].filter(isNot(isError))
 * // [1, 2]
 * ```
 * @param guard - guard function
 * @category Guard
 */
export function isNot(guard) {
    return function (data) { return !guard(data); };
}
/**
 * Checks if value is plain object, acts as a typescript safeguard.
 *
 * @example
 * ```
 * isPlainObject({a: 1})
 * // true
 *
 * isPlainObject([1])
 * // false
 * ```
 * @category Guard
 */
export function isPlainObject(value) {
    var isObject = function (v) {
        return String(v) === '[object Object]';
    };
    if (!isObject(value))
        return false;
    var constructor = value.constructor;
    if (constructor === undefined)
        return true;
    var prototype = constructor.prototype;
    if (!isObject(prototype))
        return false;
    if (!prototype.hasOwnProperty('isPrototypeOf')) {
        return false;
    }
    return true;
}
/**
 * Checks if given value is `Error` if it's subclass and then throw it.
 *
 * @example
 * ```
 * const value = new Error()
 * const value2 = assertNotError(value)
 * // throws error
 * ```
 * @category Guard
 * @param value any value
 * @returns `value` if it is not an instance of `Error`
 * @throws `value` if it is an instance of `Error`
 */
export function assertNotError(value) {
    if (value instanceof Error) {
        throw value;
    }
    return value;
}
export function ensureArray(value) {
    return value instanceof Array ? value : [value];
}
/**
 * Returns `value` if it is instance of error otherwise wraps `value` to new `Error` instance.
 *
 * @param value `Error` or other value
 * @returns `value` if it instance of error `Error` otherwise wraps `value` to new `Error` instance
 * @category Guard
 */
export function ensureError(value) {
    if (value instanceof Error) {
        return value;
    }
    return new Error(String(value));
}
/**
 * Checks if unknown object have provided keys.
 *
 * @example
 * ```
 * if (hasKeys(value, ['a', 'b'])) {
 *     console.log(value.a, value.b)
 * }
 * ```
 * @category Guard
 */
export function hasKeys(obj, keys) {
    if (typeof obj === 'object' && keys.every(function (v) { return v in obj; })) {
        return true;
    }
    return false;
}
